// Libs
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// Libs
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
// Components
import ChartCard from '@/common-utils/components/renders/chartsCards/ChartCard';
import CardRedirection from '../../common/CardRedirection';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { BarChart, Bar, LabelList, XAxis, YAxis, Cell } from 'recharts';
import { ChartContainer } from '@/common-utils/components/ui/chart';
import BarChartSkeleton from '../../common/BarChartSkeleton';
import BarOutlineBackground from '../../common/BarOutlineBackground';
// Common
import RouteUrls from '@/app/root/enums/RouteUrls';
import POS_TERMINAL_STATUS from '@/app/modules/terminals/enums/PosTerminalStatus';
import TERMINALS_CHART_VARIANTS from '../../../enums/TerminalsChartVariants';
import POSTERMINAL_STATS_FIELDS from '../../../fields/PosterminalStatsFields';
import getTranslatedText from '@/common-utils/functions/getTranslatedText';
// Lang
import { nsDashboard, nsTerminal } from '@/app/i18n/namespaces';
import genUID from '@/common-utils/functions/generateUniqueId';

/**
 * @param root0
 * @param root0.dashboardData
 * @returns {JSX} SubscriptionsStatusChart
 */
function SubscriptionsStatusChart({ dashboardData }) {

    /** Status label: fixed-width box from bar left so full text shows even when bar width is tiny; number stays past this zone or past bar end. */
    const STATUS_LABEL_PADDING_LEFT = 8;
    /** Fixed-width pill like the mockup (keeps label readable even when value is tiny). */
    const STATUS_LABEL_BOX_WIDTH = 140;
    const STATUS_ZONE_END = STATUS_LABEL_PADDING_LEFT + STATUS_LABEL_BOX_WIDTH;
    const VALUE_LABEL_GAP = 6;
    // Keep a fixed reserved area for the value label, but small enough to maximize bar length.
    const VALUE_LABEL_BOX_WIDTH = 80;
    const BAR_RADIUS = 10;

    const trackEndXByIndexRef = useRef({});

    // Hooks
    const { i18n } = useTranslation();
    const navigate = useNavigate();

    // State
    const [chartData, setChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({});

    // Hooks
    useEffect(() => {
        if (dashboardData.posTerminalAndSubscriptions?.data) {
            const statusBreakdown = Object.values(
                dashboardData.posTerminalAndSubscriptions?.data?.[POSTERMINAL_STATS_FIELDS.POS_STATUS_BREAKDOWN.key] || {}
            );

            const getCount = (statusName) => statusBreakdown.find((s) => s.status === statusName)?.count || 0;
            const buildItem = (statusEnum) => ({
                status: getTranslatedText(statusEnum.entity, nsTerminal),
                number: getCount(statusEnum.name),
                variant: statusEnum.variant,
                fill: TERMINALS_CHART_VARIANTS.STATUS[statusEnum.variant]?.fill || TERMINALS_CHART_VARIANTS.STATUS.default.fill,
                text: TERMINALS_CHART_VARIANTS.STATUS[statusEnum.variant]?.text || TERMINALS_CHART_VARIANTS.STATUS.default.text
            });

            setChartData([
                buildItem(POS_TERMINAL_STATUS.ALLOWED),
                buildItem(POS_TERMINAL_STATUS.TEMPORARILY_ALLOWED),
                buildItem(POS_TERMINAL_STATUS.BLOCKED)
            ]
                .filter((item) => item.number > 0)
                .sort((a, b) => b.number - a.number)
            );

            setChartConfig({
                number: {
                    label: getTranslatedText('charts.terminals_status.count', nsDashboard)
                },
                [POS_TERMINAL_STATUS.ALLOWED.name]: {
                    label: getTranslatedText(POS_TERMINAL_STATUS.ALLOWED.entity, nsTerminal)
                },
                [POS_TERMINAL_STATUS.TEMPORARILY_ALLOWED.name]: {
                    label: getTranslatedText(POS_TERMINAL_STATUS.TEMPORARILY_ALLOWED.entity, nsTerminal)
                },
                [POS_TERMINAL_STATUS.BLOCKED.name]: {
                    label: getTranslatedText(POS_TERMINAL_STATUS.BLOCKED.entity, nsTerminal)
                }
            });
        }
    }, [dashboardData.posTerminalAndSubscriptions?.data, i18n.language]);

    return (
        <>
            {dashboardData.posTerminalAndSubscriptions?.isFetching &&
                <BarChartSkeleton />
            }
            {!dashboardData.posTerminalAndSubscriptions?.isFetching && (
                <ChartCard
                    title='charts.terminals.status.title'
                    tooltipEntity='charts.terminals.status.tooltip'
                    namespace={nsDashboard}
                >
                    {dashboardData.posTerminalAndSubscriptions?.data
                        && !dashboardData.posTerminalAndSubscriptions?.error && (
                            <ChartContainer config={chartConfig} className='w-full max-h-[240px]'>
                                <BarChart
                                    data={chartData}
                                    layout='vertical'
                                    barSize={40}
                                    barCategoryGap={2}
                                    barGap={0}
                                    margin={{ left: 4, right: VALUE_LABEL_BOX_WIDTH, top: 0, bottom: 0 }}
                                >
                                    <YAxis
                                        dataKey='status'
                                        type='category'
                                        hide
                                    />
                                    <XAxis dataKey='number' type='number' hide />
                                    <Bar
                                        dataKey='number'
                                        radius={BAR_RADIUS}
                                        background={(props) => (
                                            <BarOutlineBackground
                                                bgProps={props}
                                                barRadius={BAR_RADIUS}
                                                trackEndXByIndexRef={trackEndXByIndexRef}
                                            />
                                        )}
                                    >
                                        {chartData.map((_, index) => (
                                            <Cell
                                                key={genUID()}
                                                fill={chartData[index]?.fill}
                                            />
                                        ))}

                                        <LabelList
                                            dataKey='status'
                                            position='insideLeft'
                                            content={(props) => {
                                                const { x = 0, y = 0, height = 0, value } = props;
                                                const itemTextColor = TERMINALS_CHART_VARIANTS.STATUS[chartData[props.index]?.variant]?.text || '#000000';

                                                return (
                                                    <foreignObject
                                                        x={x + STATUS_LABEL_PADDING_LEFT}
                                                        y={y}
                                                        width={STATUS_LABEL_BOX_WIDTH}
                                                        height={height}
                                                        style={{ overflow: 'visible' }}
                                                    >
                                                        <div
                                                            xmlns='http://www.w3.org/1999/xhtml'
                                                            className='flex h-full items-center'
                                                            style={{ overflow: 'visible' }}
                                                        >
                                                            <Entity
                                                                entity={String(value)}
                                                                className='text-sm font-medium leading-tight'
                                                                style={{ color: itemTextColor }}
                                                            />
                                                        </div>
                                                    </foreignObject>
                                                );
                                            }}
                                        />

                                        <LabelList
                                            dataKey='number'
                                            position='right'
                                            content={(props) => {
                                                const { x = 0, y = 0, width = 0, height = 0, value } = props;
                                                // Do not use <Entity> here: it runs i18n t() on the string; locale numbers are not keys and often render empty.
                                                const label =
                                                    typeof value === 'number' || typeof value === 'bigint'
                                                        ? Number(value).toLocaleString(i18n.language)
                                                        : String(value ?? '');
                                                // Get the end of the bar for the current index
                                                const trackEndX = trackEndXByIndexRef.current?.[props.index] ?? (x + width);
                                                // Add a gap between the track and the value label
                                                const afterTrack = trackEndX + VALUE_LABEL_GAP;
                                                // Add a gap between the status zone and the value label
                                                const afterStatusZone = x + STATUS_ZONE_END + VALUE_LABEL_GAP;
                                                // Get the maximum of the afterTrack and afterStatusZone
                                                const numberX = Math.max(afterTrack, afterStatusZone);
                                                // Get the text color from the payload
                                                const itemTextColor = props?.payload?.text || '#000000';

                                                return (
                                                    <foreignObject
                                                        x={numberX}
                                                        y={y}
                                                        width={VALUE_LABEL_BOX_WIDTH}
                                                        height={height}
                                                        style={{ overflow: 'visible' }}
                                                    >
                                                        <div
                                                            xmlns='http://www.w3.org/1999/xhtml'
                                                            className='flex h-full items-center'
                                                        >
                                                            <span
                                                                className='text-sm font-medium tabular-nums'
                                                                style={{ color: itemTextColor }}
                                                            >
                                                                {label}
                                                            </span>
                                                        </div>
                                                    </foreignObject>
                                                );
                                            }}
                                        />
                                    </Bar>
                                </BarChart>
                            </ChartContainer>
                        )}
                    {
                        dashboardData.posTerminalAndSubscriptions?.error && (
                            <CardRedirection
                                buttonEntity='redirection.pos_terminals'
                                namespace={nsDashboard}
                                onClick={() => { navigate(RouteUrls.SUBSCRIPTIONS.url); }}
                            />
                        )
                    }
                </ChartCard >
            )}
        </>
    );
}

SubscriptionsStatusChart.propTypes = {
    dashboardData: PropTypes.object.isRequired
};

export default SubscriptionsStatusChart;